import { ProductPreview } from "../types/conversation";
import {
  Task,
  TaskContent,
  TaskItem,
  TaskTrigger,
} from "@/components/ai-elements/task";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Search, ChevronDown } from "lucide-react";
import { useAutoCollapse } from "./useAutoCollapse";
import { Suggestion } from "@/components/ai-elements/suggestion";
import { useState, useEffect } from "react";

interface QueryProducts {
  query: string;
  queryIndex: number;
  products: ProductPreview[];
  totalResults: number;
}

export const ChainOfThought = ({
  previewSearchQueries,
  previewQueryProducts,
  isStreaming,
  hasContent,
  isLastMessage = false,
}: {
  previewSearchQueries: string[];
  previewQueryProducts: QueryProducts[];
  isStreaming: boolean;
  currentStatus?: string;
  hasContent?: boolean;
  isLastMessage?: boolean;
}) => {
  // Track which query products have been shown for staggered animation
  const [visibleQueryIndices, setVisibleQueryIndices] = useState<Set<number>>(
    new Set()
  );
  const [lastProcessedLength, setLastProcessedLength] = useState(0);
  const [hasEverStreamed, setHasEverStreamed] = useState(false);

  // Track if this component has ever been in streaming mode
  useEffect(() => {
    if (isStreaming) {
      setHasEverStreamed(true);
    }
  }, [isStreaming]);

  // Only animate if actively streaming (not loading existing data)
  useEffect(() => {
    if (!isStreaming) {
      // If not streaming, show all immediately without animation
      setVisibleQueryIndices(
        new Set(previewQueryProducts.map((qp) => qp.queryIndex))
      );
      return;
    }

    // Show new query products as they arrive with a slight stagger
    if (previewQueryProducts.length > lastProcessedLength) {
      const newProducts = previewQueryProducts.slice(lastProcessedLength);

      newProducts.forEach((queryData, relativeIdx) => {
        const delay = relativeIdx * 300; // 300ms stagger between each new one
        setTimeout(() => {
          setVisibleQueryIndices(
            (prev) => new Set([...prev, queryData.queryIndex])
          );
        }, delay);
      });

      setLastProcessedLength(previewQueryProducts.length);
    }
  }, [previewQueryProducts.length, isStreaming, lastProcessedLength]);

  // Track if all queries have products (for delayed auto-collapse)
  const allQueriesHaveProducts =
    previewSearchQueries.length > 0 &&
    previewSearchQueries.every((query) =>
      previewQueryProducts.some((qp) => qp.query === query)
    );

  // Delay auto-collapse when all products arrive during streaming
  const [shouldCollapse, setShouldCollapse] = useState(false);

  useEffect(() => {
    if (!isStreaming) {
      setShouldCollapse(false);
      return;
    }

    if (allQueriesHaveProducts) {
      // Wait 2 seconds before collapsing when all products have arrived
      const timer = setTimeout(() => {
        setShouldCollapse(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [allQueriesHaveProducts, isStreaming]);

  // Determine if section should be collapsed:
  // - Only show streaming states for the latest message
  // - If actively streaming AND has content (text after tool calls): collapse it
  // - If actively streaming but no content yet (still in tool call phase): keep open
  // - If never streamed (loaded from existing conversation): start collapsed
  // - If was streaming and now stopped: use auto-collapse logic
  const shouldCollapseQueries =
    isStreaming && isLastMessage
      ? hasContent // Collapse if streaming text after tool calls, otherwise keep open
      : !hasEverStreamed
      ? true // Collapsed if loaded from saved conversation
      : shouldCollapse; // Otherwise use auto-collapse logic

  // Manage collapsible state with auto-collapse behavior for queries section
  const [queriesOpenState, setQueriesOpen] = useAutoCollapse(
    shouldCollapseQueries ?? false
  );

  // Force open during streaming (but only if no content yet and it's the latest message) to ensure images load
  // Once content starts streaming, respect the collapse logic
  const finalOpenState =
    isStreaming && !hasContent && isLastMessage ? true : queriesOpenState;

  return (
    <div style={{ width: "100%", marginBottom: "12px" }}>
      {previewSearchQueries.length > 0 && (
        <Task
          className="tw:w-full tw:mb-3"
          open={finalOpenState}
          onOpenChange={setQueriesOpen}
        >
          <TaskTrigger title="">
            <div className="tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:gap-2 tw:text-muted-foreground tw:text-sm tw:transition-colors tw:hover:tw:text-foreground">
              <Search className="tw:size-4" />
              {isStreaming && isLastMessage && !hasContent ? (
                <Shimmer as="p" className="tw:text-sm">
                  Searching products
                </Shimmer>
              ) : (
                <p className="tw:text-sm">Search results</p>
              )}
              <ChevronDown className="tw:size-4 tw:transition-transform tw:group-data-[state=open]:tw:rotate-180" />
            </div>
          </TaskTrigger>
          <TaskContent>
            {previewSearchQueries.map((query, idx) => {
              // Find the matching query products for this query
              const matchingQueryData = previewQueryProducts.find(
                (qp) => qp.query === query
              );
              const shouldShowProducts =
                matchingQueryData &&
                visibleQueryIndices.has(matchingQueryData.queryIndex);

              return (
                <TaskItem key={`${query}-${idx}`}>
                  <Suggestion suggestion={query} className="tw:text-sm">
                    {query}
                  </Suggestion>

                  {/* Render product previews beneath this specific query */}
                  {matchingQueryData && (
                    <div
                      style={{
                        marginTop: "12px",
                        // Only animate during active streaming (not when loading existing data)
                        opacity: shouldShowProducts ? 1 : 0,
                        animation:
                          shouldShowProducts && isStreaming && hasEverStreamed
                            ? `fadeIn 0.3s ease-in forwards`
                            : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        {matchingQueryData.products
                          .slice(0, 5)
                          .map((product, productIdx) => (
                            <a
                              key={`${product.stacklineSku}-${productIdx}`}
                              href={`/placeholder/p/${product.stacklineSku}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={product.title}
                              style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "50%",
                                overflow: "hidden",
                                backgroundColor: "#fff",
                                border: "1px solid #e0e0e0",
                                flexShrink: 0,
                                position: "relative",
                                display: "block",
                                transition: "transform 0.2s, box-shadow 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.05)";
                                e.currentTarget.style.boxShadow =
                                  "0 2px 8px rgba(0,0,0,0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <img
                                src={product.imageUrl}
                                alt={product.title}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </a>
                          ))}
                        {matchingQueryData.totalResults > 5 && (
                          <div
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              backgroundColor: "#f5f5f5",
                              border: "1px solid #e0e0e0",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#666",
                              fontSize: "14px",
                              fontWeight: "500",
                            }}
                            title={`${
                              matchingQueryData.totalResults - 5
                            } more products`}
                          >
                            +{matchingQueryData.totalResults - 5}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TaskItem>
              );
            })}
          </TaskContent>
        </Task>
      )}
    </div>
  );
};
